import { useRef, useState } from 'react'
import algoliasearch, { SearchClient } from 'algoliasearch'
import useDataFetch from '@scentregroup/shared/hooks/use-data-fetch'
import { Centre } from '../../../types/contentful-v1'
import { getNowInCentreInTimeFormat } from '../../helpers/date-helpers'
import { AlgoliaApiKeys, SegmentHits, SearchContext, Segment } from './types'
import querySegments from './query-segments'

interface OffsetlessTimeGetters {
  getOffsetlessTimeInDefaultTz: () => number
  getOffsetlessTimeInCentre?: () => number
}

interface Clients {
  content: SearchClient
  marketplace: SearchClient
}

const getDefaultNationalTimezone = (country: string): string => {
  if (country === 'New Zealand') {
    return 'Pacific/Auckland'
  } else {
    return 'Australia/Sydney'
  }
}

const makeOffsetlessTimeGetters = (
  country: string,
  centre?: Centre
): OffsetlessTimeGetters => ({
  getOffsetlessTimeInDefaultTz: getNowInCentreInTimeFormat(
    getDefaultNationalTimezone(country)
  ) as () => number,
  getOffsetlessTimeInCentre:
    centre && (getNowInCentreInTimeFormat(centre.timeZone) as () => number),
})

function makeClients(algoliaApiKeys: AlgoliaApiKeys): Clients | undefined {
  const {
    contentApplicationId,
    contentApiKey,
    marketplaceApplicationId,
    marketplaceApiKey,
  } = algoliaApiKeys
  if (
    !contentApplicationId ||
    !contentApiKey ||
    !marketplaceApplicationId ||
    !marketplaceApiKey
  ) {
    return undefined
  }
  return {
    content: algoliasearch(contentApplicationId, contentApiKey),
    marketplace: algoliasearch(marketplaceApplicationId, marketplaceApiKey),
  }
}

interface FetchDataArgs {
  query: string
  clients?: Clients
  context: SearchContext
}

interface UseAlgoliaReturns {
  results: SegmentHits
  isLoading: boolean
  setQuery: (query: string) => void
  query: string
  isError: boolean
  isInitialised: boolean
}

const segments = Object.values(Segment)

const doQuery = ({
  query,
  clients,
  context,
}: FetchDataArgs): ReturnType<typeof querySegments> =>
  clients
    ? querySegments(query, clients, context, segments)
    : Promise.reject(new Error('Algolia clients not initalised'))

export default function useAlgolia(
  algoliaApiKeys: AlgoliaApiKeys,
  isEvolved: boolean,
  country: string,
  centre?: Centre,
  initialQuery?: string
): UseAlgoliaReturns {
  const [query, setQuery_] = useState(initialQuery || '')
  const [isError, setIsError] = useState(false)
  const context = {
    country,
    centre,
    isEvolved,
    ...makeOffsetlessTimeGetters(country, centre),
  }
  const clients = useRef(makeClients(algoliaApiKeys))
  const makeInput = (q: string): FetchDataArgs => ({
    clients: clients.current,
    context,
    query: q,
  })
  const fetching = useDataFetch(
    doQuery,
    (_err: Error): SegmentHits => {
      // TODO When Sentry becomes available as Context, log the err
      setIsError(true)
      return {}
    },
    initialQuery === undefined ? undefined : makeInput(initialQuery)
  )

  const setQuery = (query_: string): void => {
    setIsError(false)
    fetching.setInput(makeInput(query_))
    setQuery_(query_)
  }
  const results: SegmentHits =
    fetching.status === 'data' && query.length > 0 ? fetching.output : {}
  const isLoading = fetching.status === 'loading'
  const isInitialised = Boolean(clients.current)
  return { query, isLoading, isError, results, setQuery, isInitialised }
}
